import * as React from 'react';
import PageIFrame, { PageIFrameOwnProps } from './PageIFrame';
import { makeFrameIDForSinglePage } from '../../helpers/pages';
import { UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import { SpineItem, isSpineItemInstalled } from '../../types/epub';
import SheetScaler, { ScaleBehavior } from './SheetScaler';
import { connect } from 'react-redux';
import { frameSizeSelector, FrameSizeSelectorResult } from '../../selectors/frameSizeSelector';
import { getGlobalFrameStore } from '../../services/FrameStore';
import { FitStyle, getWindowRect } from '../../utils/view';
import { UIModeState } from '../../reducers/uimode';
import { defaultPageIFrameBackgrouncColor } from '../../constants';
import { Rectangle } from 'ark-common';
import { getNotInstalledURLFullPath } from '../../utils/browser';

export interface SingleItemViewOwnProps {
  opf: string;
  rootDir: string;
  installedComponents: ReadonlyArray<string>;
  item: SpineItem;
  panelID: string;
  uiEventListeners: UIEventListeners;
  uimode: UIModeState;
  shouldApplyReflowableContentSettings: boolean;
  topLevel: boolean;
  collapsed: boolean;
  style?: React.CSSProperties;
}

type SingleItemViewProps = SingleItemViewOwnProps & FrameSizeSelectorResult;

const renderNonZoomableIFrame = (
  panelID: string,
  frameID: string,
  path: string,
  opf: string,
  cfi: string,
  shouldApplyReflowableContentSettings: boolean,
  style?: React.CSSProperties,
) => {
  const pageIFrameProps: PageIFrameOwnProps = {
    type: 'file',
    frameInfo: {
      frameID,
      panelID,
      shouldApplyReflowableContentSettings,
      reference: [opf, cfi],
      shouldNotifyContentSize: false,
    },
    style: { ...defaultPageIFrameBackgrouncColor, ...style },
    content: path,
  };
  const wrapperStyle: React.CSSProperties = {
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    height: '100%',
  };

  return (
    <div style={wrapperStyle}>
      <PageIFrame {...pageIFrameProps} />
    </div>
  );
};

const SingleItemView: React.SFC<SingleItemViewProps> = (props) => {
  const frameID = makeFrameIDForSinglePage(props.panelID);
  const path = (
    isSpineItemInstalled(props.item, props.installedComponents)
      ? `${props.rootDir}/${props.item.path}`
      : getNotInstalledURLFullPath()
  );

  if (!props.item.fxl && props.item.fitStyle === FitStyle.FitScreen) {
    return renderNonZoomableIFrame(props.panelID,
                                   frameID,
                                   path,
                                   props.opf,
                                   props.item.epubcfi,
                                   props.shouldApplyReflowableContentSettings,
                                   props.style);
  }

  const pageIFrameProps: PageIFrameOwnProps = {
    type: 'file',
    frameInfo: {
      frameID,
      panelID: props.panelID,
      reference: [props.opf, props.item.epubcfi],
      shouldNotifyContentSize: props.item.fitStyle !== FitStyle.FitScreen,
      shouldApplyReflowableContentSettings: props.shouldApplyReflowableContentSettings,
    },
    style: { ...defaultPageIFrameBackgrouncColor, ...props.style },
    content: path,
    pointerListeners: props.uiEventListeners,
  };

  if (props.scaleBehavior !== ScaleBehavior.Resize) {
    pageIFrameProps.width = props.width;
    pageIFrameProps.height = props.height;
  }

  const frameStore = getGlobalFrameStore();
  const onScaleApply = (
    visibleRect: Rectangle,
    visualScale: number,
    tabPanelAreaRect: Rectangle,
  ) => {
    const { width, height } = props.topLevel ? getWindowRect() : tabPanelAreaRect;
    frameStore.notifyVisualProperties(
      frameID,
      visibleRect,
      visualScale,
      { width, height },
      props.collapsed,
    );
  };

  return (
    <SheetScaler behavior={props.scaleBehavior} panelID={props.panelID}
      uiEventListeners={props.uiEventListeners} uimode={props.uimode} onScaleApply={onScaleApply}>
      <PageIFrame {...pageIFrameProps} />
    </SheetScaler>
  );
};

export default connect(frameSizeSelector)(SingleItemView);
